import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { damp } from '../util/format';

/** World-unit shopping cart. Proportions follow a real cart, scaled slightly so it reads on a street. */
export const CART = {
  rideHeight: 0.5,
  wheelRadius: 0.09,
  wheelWidth: 0.045,
  mass: 48,
  collider: { hx: 0.4, hy: 0.26, hz: 0.66, y: -0.24, z: -0.06 },
};

export interface CartOptions {
  plate?: string;
  propulsion?: boolean;
  shadows?: boolean;
}

interface Caster {
  pivot: THREE.Group;
  spin: THREE.Group;
}

export class CartView {
  readonly root = new THREE.Group();
  private readonly tilt = new THREE.Group();
  private readonly casters: Caster[] = [];
  private readonly front: Caster[] = [];
  private readonly rear: Caster[] = [];
  private readonly reflector: THREE.MeshStandardMaterial;
  private spinAngle = 0;
  private steer = 0;
  private readonly flame: THREE.Group;
  private nose = 0;
  private noseVel = 0;
  private prevSpeed = 0;
  private speedReady = false;
  private wasAir = false;
  private bumpTravel = 0;

  constructor(options: CartOptions = {}) {
    const shadows = options.shadows !== false;
    const chrome = new THREE.MeshStandardMaterial({
      color: 0xe4e8ee,
      metalness: 1,
      roughness: 0.22,
      envMapIntensity: 1.15,
    });
    const boltMat = new THREE.MeshStandardMaterial({
      color: 0x9aa3ad,
      metalness: 0.85,
      roughness: 0.35,
    });
    const plastic = new THREE.MeshPhysicalMaterial({
      color: 0xd01218,
      metalness: 0.04,
      roughness: 0.32,
      clearcoat: 0.55,
      clearcoatRoughness: 0.38,
    });
    this.reflector = new THREE.MeshStandardMaterial({
      color: 0xff2a2a,
      emissive: 0xff1a1a,
      emissiveIntensity: 0.18,
      roughness: 0.4,
    });
    const rubber = new THREE.MeshStandardMaterial({ color: 0x1a1a1a, roughness: 0.92, metalness: 0 });
    const hub = new THREE.MeshStandardMaterial({ color: 0xc5ccd4, metalness: 0.9, roughness: 0.28 });

    this.root.add(this.tilt);
    this.root.name = 'cart';

    const tubes: THREE.BufferGeometry[] = [];
    const addTube = (from: THREE.Vector3, to: THREE.Vector3, radius: number, segments = 6) => {
      const direction = new THREE.Vector3().subVectors(to, from);
      const length = direction.length();
      if (length < 1e-4) return;
      const geometry = new THREE.CylinderGeometry(radius, radius, length, segments);
      const mid = new THREE.Vector3().addVectors(from, to).multiplyScalar(0.5);
      const quaternion = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), direction.normalize());
      geometry.applyMatrix4(new THREE.Matrix4().compose(mid, quaternion, new THREE.Vector3(1, 1, 1)));
      tubes.push(geometry);
    };

    const x0 = -0.36;
    const x1 = 0.36;
    const z0 = -0.55;
    const z1 = 0.52;
    const yBottom = -0.02;
    const yTop = 0.46;

    for (let x = x0; x <= x1 + 0.001; x += 0.072) {
      addTube(new THREE.Vector3(x, yBottom, z0), new THREE.Vector3(x, yBottom, z1), 0.011);
    }
    for (let z = z0; z <= z1 + 0.001; z += 0.08) {
      addTube(new THREE.Vector3(x0, yBottom, z), new THREE.Vector3(x1, yBottom, z), 0.011);
    }

    const railYs = [0.08, 0.2, 0.32, yTop];
    for (const y of railYs) {
      addTube(new THREE.Vector3(x0, y, z0), new THREE.Vector3(x1, y, z0), y === yTop ? 0.016 : 0.01);
      addTube(new THREE.Vector3(x0, y, z1), new THREE.Vector3(x1, y, z1), y === yTop ? 0.016 : 0.01);
      addTube(new THREE.Vector3(x0, y, z0), new THREE.Vector3(x0, y, z1), y === yTop ? 0.016 : 0.01);
      addTube(new THREE.Vector3(x1, y, z0), new THREE.Vector3(x1, y, z1), y === yTop ? 0.016 : 0.01);
    }

    const vertical = (x: number, z: number) => {
      addTube(new THREE.Vector3(x, yBottom, z), new THREE.Vector3(x, yTop, z), 0.01);
    };
    for (let z = z0; z <= z1 + 0.001; z += 0.09) {
      vertical(x0, z);
      vertical(x1, z);
    }
    for (let x = x0 + 0.09; x < x1 - 0.01; x += 0.09) {
      vertical(x, z0);
      vertical(x, z1);
    }

    const frameY = -0.28;
    const fx0 = -0.34;
    const fx1 = 0.34;
    const fz0 = -0.5;
    const fz1 = 0.46;
    addTube(new THREE.Vector3(fx0, frameY, fz0), new THREE.Vector3(fx1, frameY, fz0), 0.016, 8);
    addTube(new THREE.Vector3(fx0, frameY, fz1), new THREE.Vector3(fx1, frameY, fz1), 0.016, 8);
    addTube(new THREE.Vector3(fx0, frameY, fz0), new THREE.Vector3(fx0, frameY, fz1), 0.016, 8);
    addTube(new THREE.Vector3(fx1, frameY, fz0), new THREE.Vector3(fx1, frameY, fz1), 0.016, 8);
    addTube(new THREE.Vector3(fx0, frameY, fz0), new THREE.Vector3(fx1, frameY, fz1), 0.012, 6);
    addTube(new THREE.Vector3(fx1, frameY, fz0), new THREE.Vector3(fx0, frameY, fz1), 0.012, 6);

    const mounts: Array<[number, number]> = [
      [-0.4, 0.42],
      [0.4, 0.42],
      [-0.4, -0.48],
      [0.4, -0.48],
    ];
    for (const [x, z] of mounts) {
      addTube(new THREE.Vector3(Math.sign(x) * 0.34, frameY, z > 0 ? fz1 : fz0), new THREE.Vector3(x, frameY, z), 0.014, 6);
      addTube(new THREE.Vector3(x, frameY, z), new THREE.Vector3(x, -0.36, z), 0.013, 6);
    }

    addTube(new THREE.Vector3(-0.32, yTop, z0), new THREE.Vector3(-0.42, 0.62, -0.7), 0.014, 8);
    addTube(new THREE.Vector3(0.32, yTop, z0), new THREE.Vector3(0.42, 0.62, -0.7), 0.014, 8);
    addTube(new THREE.Vector3(-0.32, 0.16, z0), new THREE.Vector3(-0.4, 0.42, -0.66), 0.01, 6);
    addTube(new THREE.Vector3(0.32, 0.16, z0), new THREE.Vector3(0.4, 0.42, -0.66), 0.01, 6);

    const wireGeo = mergeGeometries(tubes, false);
    if (!wireGeo) throw new Error('Falha ao montar a cesta do carrinho');
    tubes.forEach((geometry) => geometry.dispose());
    const wires = new THREE.Mesh(wireGeo, chrome);
    wires.castShadow = shadows;
    wires.receiveShadow = shadows;
    this.tilt.add(wires);

    const bolts: THREE.BufferGeometry[] = [];
    const boltGeo = new THREE.CylinderGeometry(0.018, 0.018, 0.02, 6);
    for (const [x, z] of mounts) {
      const geometry = boltGeo.clone();
      geometry.translate(x, -0.35, z);
      bolts.push(geometry);
      const side = boltGeo.clone();
      side.rotateX(Math.PI / 2);
      side.translate(x, frameY, z);
      bolts.push(side);
    }
    const boltMerged = mergeGeometries(bolts, false);
    boltGeo.dispose();
    if (boltMerged) {
      const boltMesh = new THREE.Mesh(boltMerged, boltMat);
      boltMesh.castShadow = shadows;
      this.tilt.add(boltMesh);
    }

    const bumperGeo = new THREE.SphereGeometry(0.055, 12, 10);
    const bumperPositions = [
      new THREE.Vector3(fx0, frameY, fz0),
      new THREE.Vector3(fx1, frameY, fz0),
      new THREE.Vector3(fx0, frameY, fz1),
      new THREE.Vector3(fx1, frameY, fz1),
    ];
    for (const position of bumperPositions) {
      const bumper = new THREE.Mesh(bumperGeo, plastic);
      bumper.position.copy(position);
      bumper.castShadow = shadows;
      this.tilt.add(bumper);
    }
    const frontBar = new THREE.Mesh(new THREE.CylinderGeometry(0.02, 0.02, 0.74, 10), plastic);
    frontBar.rotation.z = Math.PI / 2;
    frontBar.position.set(0, 0.16, z1 + 0.02);
    frontBar.castShadow = shadows;
    this.tilt.add(frontBar);

    const handle = new THREE.Mesh(new THREE.CylinderGeometry(0.028, 0.028, 0.86, 12), plastic);
    handle.rotation.z = Math.PI / 2;
    handle.position.set(0, 0.62, -0.7);
    handle.castShadow = shadows;
    this.tilt.add(handle);

    const gripMat = new THREE.MeshStandardMaterial({ color: 0xf4f1e6, roughness: 0.55 });
    for (const x of [-0.22, 0.22]) {
      const grip = new THREE.Mesh(new THREE.CylinderGeometry(0.032, 0.032, 0.12, 10), gripMat);
      grip.rotation.z = Math.PI / 2;
      grip.position.set(x, 0.62, -0.7);
      this.tilt.add(grip);
    }

    for (const x of [-0.22, 0.22]) {
      const reflector = new THREE.Mesh(new THREE.BoxGeometry(0.06, 0.035, 0.02), this.reflector);
      reflector.position.set(x, frameY + 0.02, fz0 - 0.02);
      this.tilt.add(reflector);
    }

    const plate = this.makePlate(options.plate ?? '1');
    plate.position.set(0, 0.22, z1 + 0.045);
    this.tilt.add(plate);

    const brand = this.makeBrand();
    brand.position.set(0, 0.56, -0.64);
    brand.rotation.x = -0.4;
    this.tilt.add(brand);

    if (options.propulsion) {
      const ring = new THREE.Mesh(
        new THREE.TorusGeometry(0.36, 0.022, 8, 28),
        new THREE.MeshStandardMaterial({
          color: 0xb9e4ff,
          emissive: 0x3aa4ff,
          emissiveIntensity: 0.85,
          metalness: 0.55,
          roughness: 0.28,
        }),
      );
      ring.rotation.x = Math.PI / 2;
      ring.position.y = -0.36;
      this.tilt.add(ring);
      const pod = new THREE.Mesh(
        new THREE.BoxGeometry(0.18, 0.05, 0.12),
        new THREE.MeshStandardMaterial({ color: 0xd0d6de, metalness: 0.8, roughness: 0.3, emissive: 0x123044, emissiveIntensity: 0.4 }),
      );
      pod.position.set(0, -0.33, -0.1);
      this.tilt.add(pod);
    }

    const wheelGeo = new THREE.CylinderGeometry(CART.wheelRadius, CART.wheelRadius, CART.wheelWidth, 16);
    wheelGeo.rotateZ(Math.PI / 2);
    const hubGeo = new THREE.CylinderGeometry(0.035, 0.035, CART.wheelWidth + 0.01, 10);
    hubGeo.rotateZ(Math.PI / 2);

    const addCaster = (x: number, z: number, frontWheel: boolean) => {
      const pivot = new THREE.Group();
      pivot.position.set(x, -0.36, z);
      const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.015, 0.015, 0.08, 8), chrome);
      shaft.position.y = 0.02;
      pivot.add(shaft);
      const forkL = new THREE.Mesh(new THREE.BoxGeometry(0.012, 0.07, 0.03), chrome);
      forkL.position.set(-0.03, -0.03, 0);
      const forkR = forkL.clone();
      forkR.position.x = 0.03;
      pivot.add(forkL, forkR);
      const spin = new THREE.Group();
      spin.position.y = -0.05;
      const tire = new THREE.Mesh(wheelGeo, rubber);
      tire.castShadow = shadows;
      const cap = new THREE.Mesh(hubGeo, hub);
      spin.add(tire, cap);
      const axle = new THREE.Mesh(new THREE.CylinderGeometry(0.01, 0.01, 0.09, 6), boltMat);
      axle.rotation.z = Math.PI / 2;
      spin.add(axle);
      pivot.add(spin);
      this.tilt.add(pivot);
      const caster = { pivot, spin };
      this.casters.push(caster);
      if (frontWheel) this.front.push(caster);
      else this.rear.push(caster);
    };

    addCaster(-0.4, 0.42, true);
    addCaster(0.4, 0.42, true);
    addCaster(-0.4, -0.48, false);
    addCaster(0.4, -0.48, false);

    this.flame = new THREE.Group();
    this.flame.name = 'boost-flame';
    this.flame.position.set(0, -0.2, -0.92);
    this.flame.visible = false;
    const flameMat = new THREE.MeshBasicMaterial({ color: 0xff7a18, transparent: true, opacity: 0.88, depthWrite: false });
    const coreMat = new THREE.MeshBasicMaterial({ color: 0xfff1c4, transparent: true, opacity: 0.95, depthWrite: false });
    for (const x of [-0.16, 0, 0.16]) {
      const cone = new THREE.Mesh(
        new THREE.ConeGeometry(x === 0 ? 0.08 : 0.055, x === 0 ? 0.52 : 0.34, 8),
        x === 0 ? coreMat : flameMat,
      );
      cone.rotation.x = -Math.PI / 2;
      cone.position.set(x, 0, -0.16);
      this.flame.add(cone);
    }
    this.root.add(this.flame);

    this.root.traverse((child) => {
      const mesh = child as THREE.Mesh;
      if (mesh.isMesh) {
        mesh.castShadow = shadows;
        // The cart used to receive its own shadow. On the chrome tubes that
        // swims every frame and reads as flicker, especially while turning.
        mesh.receiveShadow = false;
      }
    });
  }

  private makePlate(text: string): THREE.Mesh {
    const canvas = document.createElement('canvas');
    canvas.width = 128;
    canvas.height = 80;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new THREE.Mesh(new THREE.BoxGeometry(0.16, 0.1, 0.01));
    ctx.fillStyle = '#f7f4ea';
    ctx.fillRect(0, 0, 128, 80);
    ctx.strokeStyle = '#202020';
    ctx.lineWidth = 6;
    ctx.strokeRect(4, 4, 120, 72);
    ctx.fillStyle = '#161616';
    ctx.font = '700 54px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, 64, 44);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    const mesh = new THREE.Mesh(
      new THREE.BoxGeometry(0.18, 0.11, 0.012),
      new THREE.MeshStandardMaterial({ map: texture, roughness: 0.6, metalness: 0.05 }),
    );
    return mesh;
  }

  private makeBrand(): THREE.Mesh {
    const canvas = document.createElement('canvas');
    canvas.width = 256;
    canvas.height = 64;
    const ctx = canvas.getContext('2d');
    if (!ctx) return new THREE.Mesh(new THREE.BoxGeometry(0.28, 0.07, 0.008));
    ctx.fillStyle = '#f5d90a';
    ctx.fillRect(0, 0, 256, 64);
    ctx.fillStyle = '#1a1a1a';
    ctx.font = '700 28px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('MEMOCART', 128, 34);
    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;
    return new THREE.Mesh(
      new THREE.BoxGeometry(0.32, 0.08, 0.01),
      new THREE.MeshStandardMaterial({ map: texture, roughness: 0.55 }),
    );
  }

  setPose(position: THREE.Vector3, quaternion: THREE.Quaternion): void {
    this.root.position.copy(position);
    // Wheels rest on the road plane. Without this gap their triangles z-fight
    // the asphalt, and steering the front casters makes that flicker obvious.
    this.root.position.y += 0.04;
    this.root.quaternion.copy(quaternion);
  }

  update(
    dt: number,
    steerAngle: number,
    speed: number,
    _lateral: number,
    braking: boolean,
    airborne: boolean,
    rough = false,
    frontBounce = false,
    boosting = false,
  ): void {
    this.steer = damp(this.steer, steerAngle, dt, 14);
    for (const caster of this.front) caster.pivot.rotation.y = this.steer;
    for (const caster of this.rear) caster.pivot.rotation.y = 0;
    this.spinAngle += (speed / CART.wheelRadius) * dt;
    for (const caster of this.casters) caster.spin.rotation.x = this.spinAngle;
    this.flame.visible = boosting;
    this.reflector.emissiveIntensity = braking ? 1.5 : boosting ? 0.7 : 0.18;
    this.applyFrontBounce(dt, speed, braking, airborne, rough, frontBounce);
  }

  /** Nose pitch around the rear axle. Off unless the player enables the setting. No idle bob. */
  private applyFrontBounce(dt: number, speed: number, braking: boolean, airborne: boolean, rough: boolean, enabled: boolean): void {
    if (!enabled) {
      this.nose = 0;
      this.noseVel = 0;
      this.bumpTravel = 0;
      this.wasAir = airborne;
      this.prevSpeed = speed;
      this.speedReady = true;
      this.tilt.rotation.set(0, 0, 0);
      this.tilt.position.set(0, 0, 0);
      return;
    }
    const step = Math.max(dt, 0.001);
    if (!this.speedReady) {
      this.prevSpeed = speed;
      this.speedReady = true;
    }
    const accel = (speed - this.prevSpeed) / step;
    this.prevSpeed = speed;
    let target = THREE.MathUtils.clamp(-accel * 0.0032, -0.06, 0.07);
    if (braking) target += 0.045;
    if (airborne) target -= 0.03;
    if (this.wasAir && !airborne) this.noseVel += 1.15;
    this.wasAir = airborne;
    if (rough && Math.abs(speed) > 3) {
      this.bumpTravel += Math.abs(speed) * dt;
      if (this.bumpTravel > 3.4) {
        this.bumpTravel = 0;
        this.noseVel += 0.8;
      }
    } else {
      this.bumpTravel = 0;
    }
    this.noseVel += (target - this.nose) * 70 * dt - this.noseVel * 11 * dt;
    this.nose = THREE.MathUtils.clamp(this.nose + this.noseVel * dt, -0.1, 0.08);
    const axleY = -0.36;
    const axleZ = -0.48;
    const c = Math.cos(this.nose);
    const s = Math.sin(this.nose);
    const y2 = axleY * c - axleZ * s;
    const z2 = axleY * s + axleZ * c;
    this.tilt.rotation.set(this.nose, 0, 0);
    this.tilt.position.set(0, axleY - y2, axleZ - z2);
  }

  dispose(): void {
    this.root.traverse((child) => {
      const mesh = child as THREE.Mesh;
      mesh.geometry?.dispose();
      const materials = Array.isArray(mesh.material) ? mesh.material : mesh.material ? [mesh.material] : [];
      for (const material of materials) {
        const map = (material as THREE.MeshStandardMaterial).map;
        if (map?.image instanceof HTMLCanvasElement) map.dispose();
        material.dispose();
      }
    });
  }
}
